import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Header() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [isMobileLearnOpen, setIsMobileLearnOpen] = useState(false);
  const [isLearnOpen, setIsLearnOpen] = useState(false);
  const [showMobileFAB, setShowMobileFAB] = useState(false);
  const logoClickCount = useRef(0);
  const logoClickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const servicesRef = useRef<HTMLDivElement>(null);
  const learnRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 50);
      if (isMobileMenuOpen) setIsMobileMenuOpen(false);
      setIsServicesOpen(false);
      setIsLearnOpen(false);
      // Show FAB after scrolling past hero (~300px)
      setShowMobileFAB(scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!isServicesOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target as Node)) {
        setIsServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isServicesOpen]);

  useEffect(() => {
    if (!isLearnOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (learnRef.current && !learnRef.current.contains(e.target as Node)) {
        setIsLearnOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isLearnOpen]);

  useEffect(() => {
    if (!showAdminPanel) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('#admin-panel') && !target.closest('#logo-btn')) {
        setShowAdminPanel(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAdminPanel]);

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    logoClickCount.current += 1;
    if (logoClickTimer.current) clearTimeout(logoClickTimer.current);
    if (logoClickCount.current >= 5) {
      logoClickCount.current = 0;
      setShowAdminPanel(prev => !prev);
    } else {
      logoClickTimer.current = setTimeout(() => {
        if (logoClickCount.current < 5) {
          logoClickCount.current = 0;
          navigate('/');
        }
      }, 600);
    }
  };

  const serviceLinks = [
    { href: '/term-life', label: 'Term Life Insurance', icon: 'ri-time-line', desc: 'Affordable coverage for a set period' },
    { href: '/whole-life', label: 'Whole Life Insurance', icon: 'ri-heart-pulse-line', desc: 'Lifetime protection & cash value' },
    { href: '/final-expense', label: 'Final Expense', icon: 'ri-shield-line', desc: 'Cover end-of-life costs' },
    { href: '/mortgage-protection', label: 'Mortgage Protection', icon: 'ri-home-heart-line', desc: 'Keep your family in their home' },
    { href: '/annuities', label: 'Annuities', icon: 'ri-funds-line', desc: 'Guaranteed retirement income' },
    { href: '/indexed-universal-life', label: 'Indexed Universal Life', icon: 'ri-line-chart-line', desc: 'Market-linked growth + protection' },
    { href: '/universal-life', label: 'Universal Life', icon: 'ri-shield-star-line', desc: 'Flexible permanent coverage' },
    { href: '/no-medical-exam', label: 'No Medical Exam', icon: 'ri-stethoscope-line', desc: 'Fast approval, no exam required' },
    { href: '/wealth-building', label: 'Wealth Building', icon: 'ri-plant-line', desc: 'Build a legacy for your family' },
    { href: '/generational-wealth', label: 'Generational Wealth', icon: 'ri-community-line', desc: 'Multi-generational financial planning' },
  ];

  const learnLinks = [
    {
      href: '/learning-hub',
      label: 'Learning Hub',
      icon: 'ri-map-2-line',
      desc: '27 guides organized by topic cluster',
      color: '#D97642',
    },
    {
      href: '/blog',
      label: 'Blog',
      icon: 'ri-article-line',
      desc: 'Expert articles — searchable & filterable',
      color: '#1A3A52',
    },
    {
      href: '/resources',
      label: 'Resources',
      icon: 'ri-folder-open-line',
      desc: 'In-depth guides & downloadable tools',
      color: '#D97642',
    },
    {
      href: '/faq',
      label: 'FAQ',
      icon: 'ri-question-answer-line',
      desc: 'Common questions answered by our team',
      color: '#1A3A52',
    },
  ];

  const adminLinks = [
    { href: '/dashboard', label: 'Dashboard', icon: 'ri-bar-chart-2-line' },
    { href: '/ghl-guide', label: 'GHL Guide', icon: 'ri-book-2-line' },
    { href: '/ghl-cheatsheet', label: 'GHL Cheat Sheet', icon: 'ri-file-list-3-line' },
    { href: '/setup-test', label: 'Setup Test', icon: 'ri-checkbox-multiple-line' },
    { href: '/nurture-workflows', label: 'Nurture Workflows', icon: 'ri-flow-chart' },
  ];

  return (
    <>
      {/* Desktop Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <button
              id="logo-btn"
              onClick={handleLogoClick}
              className="flex items-center gap-3 group cursor-pointer bg-transparent border-none p-0 relative"
              aria-label="Home"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`ri-shield-check-fill text-[18px] transition-colors duration-300 ${!isScrolled ? 'text-[#D97642]' : 'text-[#D97642]'}`}></i>
              </div>
              <span className={`text-lg font-bold tracking-tight transition-colors duration-300 whitespace-nowrap ${!isScrolled ? 'text-white' : 'text-[#1A3A52]'}`}>
                Hoosier Life Insurance
              </span>

              {showAdminPanel && (
                <div
                  id="admin-panel"
                  className="absolute top-14 left-0 w-52 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl overflow-hidden z-50"
                  onClick={e => e.stopPropagation()}
                >
                  <div className="px-4 py-2.5 border-b border-gray-700 flex items-center gap-2">
                    <i className="ri-lock-2-line text-[#D97642] text-sm"></i>
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Admin Access</span>
                  </div>
                  {adminLinks.map(link => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-gray-200 hover:bg-[#D97642]/30 hover:text-white transition-colors"
                    >
                      <i className={`${link.icon} text-[#D97642]`}></i>
                      {link.label}
                    </a>
                  ))}
                </div>
              )}
            </button>

            {/* Desktop Navigation — 5 focused items */}
            <nav className="hidden lg:flex items-center gap-1.5">

              {/* 1. Services Dropdown */}
              <div ref={servicesRef} className="relative">
                <button
                  onClick={() => setIsServicesOpen(prev => !prev)}
                  className="px-3.5 py-2.5 bg-[#1A3A52] text-white text-sm font-medium rounded-lg hover:bg-[#0F2A3F] transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer"
                >
                  Services
                  <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {isServicesOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl border border-gray-100 overflow-hidden z-50" style={{boxShadow: '0 8px 32px rgba(0,0,0,0.12)'}}>
                    <div className="px-4 py-2.5 border-b border-gray-100">
                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Our Products</span>
                    </div>
                    {serviceLinks.map(link => (
                      <a
                        key={link.href}
                        href={link.href}
                        onClick={() => setIsServicesOpen(false)}
                        className="flex items-start gap-3 px-4 py-2.5 hover:bg-[#F9F7F4] transition-colors group"
                      >
                        <div className="w-7 h-7 flex items-center justify-center bg-[#D97642]/10 rounded-lg group-hover:bg-[#D97642]/20 transition-colors flex-shrink-0 mt-0.5">
                          <i className={`${link.icon} text-[#D97642] text-sm`}></i>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-800 group-hover:text-[#D97642] transition-colors">{link.label}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{link.desc}</div>
                        </div>
                      </a>
                    ))}
                    <div className="px-4 py-3 bg-[#F9F7F4] border-t border-gray-100">
                      <a href="/all-services" onClick={() => setIsServicesOpen(false)} className="text-xs font-semibold text-[#D97642] hover:text-[#C26638] flex items-center gap-1">
                        View all services <i className="ri-arrow-right-line"></i>
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* 2. About */}
              <a
                href="/#about"
                className={`px-3.5 py-2.5 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${!isScrolled ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-[#1A3A52] hover:bg-gray-100'}`}
              >
                About
              </a>

              {/* 3. Service Areas */}
              <a
                href="/service-areas"
                className={`px-3.5 py-2.5 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${!isScrolled ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-[#1A3A52] hover:bg-gray-100'}`}
              >
                Service Areas
              </a>

              {/* 4. Phone */}
              <a
                href="tel:+13176482983"
                className={`px-3.5 py-2.5 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-1.5 ${!isScrolled ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-[#1A3A52] hover:bg-gray-100'}`}
              >
                <i className="ri-phone-line"></i>
                (317) 648-2983
              </a>

              {/* 5. Book Consultation — primary CTA */}
              <a
                href="/book-appointment"
                className="px-5 py-2.5 bg-[#D97642] text-white text-sm font-bold rounded-lg hover:bg-[#C26638] transition-colors whitespace-nowrap flex items-center gap-1.5"
              >
                <i className="ri-calendar-check-line"></i>
                Book a Free Consultation
              </a>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-nav-menu"
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
                isScrolled ? 'text-gray-900 hover:bg-gray-100' : 'text-white hover:bg-white/10'
              }`}
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`} aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setIsMobileMenuOpen(false)}></div>
          <div id="mobile-nav-menu" className="fixed top-20 left-0 right-0 bg-white overflow-y-auto" style={{maxHeight: 'calc(100vh - 5rem - 72px)'}}>
            <nav aria-label="Mobile navigation" className="flex flex-col p-4 gap-0.5">

              {/* Services Accordion */}
              <button
                onClick={() => setIsMobileServicesOpen(prev => !prev)}
                className="px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-between cursor-pointer w-full text-left font-medium"
              >
                <span>Services</span>
                <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${isMobileServicesOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {isMobileServicesOpen && (
                <div className="ml-4 mb-1 border-l-2 border-[#D97642]/20 pl-3">
                  {serviceLinks.map(link => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 text-gray-600 hover:text-[#D97642] hover:bg-[#F9F7F4] rounded-lg transition-colors"
                    >
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className={`${link.icon} text-[#D97642] text-sm`}></i>
                      </div>
                      <span className="text-sm">{link.label}</span>
                    </a>
                  ))}
                  <a href="/all-services" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-2 px-3 py-2.5 text-[#D97642] font-semibold text-sm">
                    <i className="ri-apps-line text-sm"></i> View all services
                  </a>
                </div>
              )}

              {/* Quiz */}
              <a
                href="/quiz"
                className="flex items-center gap-2 px-4 py-3 text-[#D97642] hover:bg-[#FFF5EF] font-bold transition-colors rounded-lg"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <i className="ri-questionnaire-line"></i>
                Free Quiz — Find Your Coverage
              </a>

              {/* Learn Accordion */}
              <button
                onClick={() => setIsMobileLearnOpen(prev => !prev)}
                className="px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-between cursor-pointer w-full text-left font-medium"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-book-open-line text-[#1A3A52]"></i>
                  Learn
                </span>
                <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${isMobileLearnOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {isMobileLearnOpen && (
                <div className="ml-4 mb-1 border-l-2 border-[#D97642]/20 pl-3">
                  {learnLinks.map(link => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 text-gray-600 hover:text-[#D97642] hover:bg-[#F9F7F4] rounded-lg transition-colors"
                    >
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className={`${link.icon} text-sm`} style={{color: link.color}}></i>
                      </div>
                      <span className="text-sm">{link.label}</span>
                    </a>
                  ))}
                </div>
              )}

              <a href="/#about" onClick={() => setIsMobileMenuOpen(false)} className="px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors font-medium">About</a>
              <a href="/service-areas" onClick={() => setIsMobileMenuOpen(false)} className="px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors font-medium">Service Areas</a>
              <a href="/contact" onClick={() => setIsMobileMenuOpen(false)} className="px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors font-medium">Contact</a>
              <a href="tel:+13176482983" className="px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors font-medium flex items-center gap-2">
                <i className="ri-phone-line text-[#1A3A52]"></i>
                (317) 648-2983
              </a>

              <a
                href="/book-appointment"
                onClick={() => setIsMobileMenuOpen(false)}
                className="mt-2 px-4 py-3.5 bg-[#D97642] text-white text-center rounded-lg hover:bg-[#C26638] transition-colors font-bold whitespace-nowrap"
              >
                Get a Free Quote
              </a>

              {/* Admin Section */}
              {showAdminPanel && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="px-4 py-1 flex items-center gap-2 mb-1">
                    <i className="ri-lock-2-line text-[#D97642] text-xs"></i>
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Admin</span>
                  </div>
                  {adminLinks.map(link => (
                    <a key={link.href} href={link.href} onClick={() => setIsMobileMenuOpen(false)} className="px-4 py-3 text-[#D97642] hover:bg-[#F9F7F4] rounded-lg transition-colors flex items-center gap-2">
                      <i className={link.icon}></i>
                      {link.label}
                    </a>
                  ))}
                </div>
              )}
            </nav>
          </div>
        </div>
      )}

      {/* Mobile Floating "Get a Free Quote" FAB — always visible after scrolling, hidden when menu is open */}
      {!isMobileMenuOpen && (
        <div
          className={`lg:hidden fixed bottom-6 right-4 z-40 transition-all duration-300 ${
            showMobileFAB ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0 pointer-events-none'
          }`}
        >
          <a
            href="/book-appointment"
            className="flex items-center gap-2 px-5 py-3.5 bg-[#D97642] text-white text-sm font-bold rounded-full whitespace-nowrap cursor-pointer"
            style={{boxShadow: '0 4px 20px rgba(217,118,66,0.50)'}}
            aria-label="Get a free life insurance quote"
          >
            <i className="ri-shield-check-fill text-base"></i>
            Get a Free Quote
          </a>
        </div>
      )}
    </>
  );
}
